import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';

// icons
import { Key, ArrowLeft } from 'iconsax-react';

// project imports
import { verifyOtp } from '../../services/authApi';

// validation schema
const validationSchema = Yup.object({
  otp: Yup.string()
    .length(6, 'Verification code must be exactly 6 digits')
    .matches(/^\d{6}$/, 'Verification code must be numeric')
    .required('Verification code is required')
});

// ==============================|| VERIFY OTP PAGE ||============================== //

const VerifyOtp = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const storedEmail = sessionStorage.getItem('forgot_email');
    if (!storedEmail) {
      toast({
        title: "Session Expired",
        description: "No email found in session. Redirecting to forgot password...",
        variant: "destructive"
      });
      const timer = setTimeout(() => {
        navigate('/forgot-password');
      }, 2000);
      return () => clearTimeout(timer);
    }
    setEmail(storedEmail);
  }, [navigate, toast]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const result = await verifyOtp(email, values.otp);
      if (result.success) {
        toast({
          title: "Verification Successful",
          description: "Your verification code is verified successfully!",
        });
        setTimeout(() => {
          navigate('/reset-password');
        }, 1500);
      } else {
        toast({
          title: "Verification Failed",
          description: result.message || 'Invalid code. Please try again.',
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      toast({
        title: "Verification Failed",
        description: err.response?.data?.message || 'Verification failed. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-[400px] border-border shadow-lg">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              Verify Code
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              We have sent a verification code to:
            </p>
            <p className="text-sm font-semibold text-foreground mt-1 break-all">
              {email || 'Loading...'}
            </p>
          </div>

          <Formik
            initialValues={{ otp: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form className="space-y-4">
                {/* OTP Input */}
                <div className="space-y-1.5">
                  <Label htmlFor="otp" className="block text-center mb-1">
                    6-Digit Verification Code
                  </Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                      <Key size={20} />
                    </div>
                    <Input
                      id="otp"
                      name="otp"
                      type="text"
                      maxLength={6}
                      placeholder="000000"
                      value={values.otp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`pl-10 text-center text-xl font-extrabold tracking-[0.25em] h-12 uppercase ${
                        touched.otp && errors.otp ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                  </div>
                  {touched.otp && errors.otp && (
                    <p className="text-xs text-destructive text-center">{errors.otp}</p>
                  )}
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 shadow-md transition-all duration-200 mt-2 flex items-center justify-center gap-2"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {isSubmitting ? 'Verifying Code...' : 'Verify Code'}
                </Button>

                {/* Change Email */}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/forgot-password')}
                  className="w-full text-muted-foreground hover:text-foreground text-sm font-medium flex items-center justify-center gap-1.5 h-10 mt-2"
                >
                  <ArrowLeft size={16} />
                  Change Email
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyOtp;
